import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Filter,
  Loader2,
  RefreshCw,
  Shield,
  Sparkles,
  Zap,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const AUTO_REFRESH_INTERVAL_MS = 30000;

const TIME_WINDOWS = [
  { value: 60, label: "Senaste timmen" },
  { value: 360, label: "Senaste 6 timmar" },
  { value: 1440, label: "Senaste 24 timmar" },
  { value: 4320, label: "Senaste 3 dygn" },
  { value: 10080, label: "Senaste 7 dygn" },
];

const RUNTIME_OPTIONS = [
  { value: "", label: "Alla" },
  { value: "blueprint", label: "blueprint" },
  { value: "legacy", label: "legacy" },
];

const RUNTIME_SOURCE_OPTIONS = [
  { value: "", label: "Alla" },
  { value: "blueprint", label: "blueprint" },
  { value: "handymen", label: "handymen" },
];

const SCOPE_OPTIONS = [
  { value: "", label: "Alla" },
  { value: "in_scope", label: "in_scope" },
  { value: "out_of_scope", label: "out_of_scope" },
  { value: "uncertain", label: "uncertain" },
  { value: "do_not_guess_match", label: "do_not_guess_match" },
];

const LLM_OPTIONS = [
  { value: "", label: "Alla" },
  { value: "true", label: "LLM anropad" },
  { value: "false", label: "Ej LLM" },
];

const LEAD_FINALISED_OPTIONS = [
  { value: "", label: "Alla" },
  { value: "true", label: "Lead skapad" },
  { value: "false", label: "Ej lead" },
];

const DEFAULT_FILTERS = {
  client_id: "elido_official",
  runtime: "",
  runtime_source: "",
  scope_verdict: "",
  fallback_reason: "",
  llm_called: "",
  error_type: "",
  lead_finalised: "",
  since_minutes: 1440,
  limit: 100,
};

function fmtTimestamp(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("sv-SE", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
}

function runtimePill(rt) {
  const isBp = rt === "blueprint";
  const cls = isBp
    ? "bg-emerald-100 text-emerald-800 border-emerald-200"
    : "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-mono uppercase border ${cls}`}>
      {isBp ? <Sparkles className="w-3 h-3" /> : <Activity className="w-3 h-3" />}
      {rt || "—"}
    </span>
  );
}

function scopePill(sv) {
  if (!sv) return <span className="text-gray-400">—</span>;
  const map = {
    in_scope: "bg-emerald-50 text-emerald-700 border-emerald-200",
    out_of_scope: "bg-amber-50 text-amber-700 border-amber-200",
    uncertain: "bg-orange-50 text-orange-700 border-orange-200",
    do_not_guess_match: "bg-rose-50 text-rose-700 border-rose-200",
  };
  const cls = map[sv] || "bg-gray-50 text-gray-700 border-gray-200";
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-[11px] font-mono border ${cls}`}>
      {sv}
    </span>
  );
}

function SummaryCard({ label, value, hint, warn = false, testid }) {
  return (
    <div
      className={`rounded-lg border p-4 ${warn
        ? "bg-rose-50 border-rose-200"
        : "bg-white border-gray-200"}`}
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-wide text-gray-500 flex items-center gap-1">
        {warn && <AlertTriangle className="w-3 h-3 text-rose-600" />}
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${warn ? "text-rose-700" : "text-gray-900"}`}>
        {value}
      </div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

export default function RuntimeEventsMonitor() {
  const { getAuthHeaders } = useAuth();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const filtersQS = useMemo(() => {
    const q = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined) return;
      q.append(k, String(v));
    });
    return q.toString();
  }, [filters]);

  const summaryQS = useMemo(() => {
    const q = new URLSearchParams();
    if (filters.client_id) q.append("client_id", filters.client_id);
    if (filters.since_minutes) q.append("since_minutes", String(filters.since_minutes));
    return q.toString();
  }, [filters.client_id, filters.since_minutes]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [evResp, sumResp] = await Promise.all([
        fetch(`${API_URL}/api/admin/runtime/events?${filtersQS}`, {
          credentials: "include",
          headers: { ...(getAuthHeaders?.() || {}) },
        }),
        fetch(`${API_URL}/api/admin/runtime/summary?${summaryQS}`, {
          credentials: "include",
          headers: { ...(getAuthHeaders?.() || {}) },
        }),
      ]);
      if (!evResp.ok) throw new Error(`Events HTTP ${evResp.status}`);
      if (!sumResp.ok) throw new Error(`Summary HTTP ${sumResp.status}`);
      const evData = await evResp.json();
      const sumData = await sumResp.json();
      setEvents(evData.events || []);
      setSummary(sumData);
      setLastFetchedAt(new Date());
    } catch (e) {
      toast.error(`Kunde inte hämta runtime-events: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, [filtersQS, summaryQS, getAuthHeaders]);

  // Initial fetch + on filter change
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const t = setInterval(fetchAll, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [autoRefresh, fetchAll]);

  const setFilter = (key, val) =>
    setFilters((f) => ({ ...f, [key]: val }));

  const resetFilters = () => setFilters(DEFAULT_FILTERS);

  // ── Summary card data ────────────────────────────────────────────
  const warns = summary?.warnings || {};
  const byRuntime = summary?.by_runtime || {};
  const byScope = summary?.by_scope || {};

  return (
    <div data-testid="runtime-events-monitor" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Activity className="w-6 h-6 text-[#255E69]" />
            Runtime-events monitor
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Read-only observability av blueprint-runtime. PII-säker —
            inga råa meddelanden eller svar lagras eller visas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label
            className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none"
            data-testid="runtime-events-auto-refresh-toggle-label"
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 accent-[#255E69]"
              data-testid="runtime-events-auto-refresh-toggle"
            />
            Auto-refresh (30s)
          </label>
          <button
            type="button"
            onClick={fetchAll}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-[#255E69] text-white hover:bg-[#1e4d56] disabled:opacity-60"
            data-testid="runtime-events-refresh-btn"
          >
            {loading
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RefreshCw className="w-4 h-4" />}
            Uppdatera
          </button>
        </div>
      </div>

      {/* PII safety banner */}
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex items-start gap-2"
           data-testid="runtime-events-pii-banner">
        <Shield className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <div>
          <div className="font-semibold">PII-säker monitor</div>
          <div className="text-emerald-800">
            Inga råa <code>user_message</code> eller <code>response</code>{" "}
            lagras eller exponeras. Felmeddelanden och snippets är PII-maskade.
            Endast metadata (latens, scope, runtime-källa, fel-typ) syns här.
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div
        className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
        data-testid="runtime-events-summary"
      >
        <SummaryCard
          label="Events"
          value={summary?.total_events ?? 0}
          hint={lastFetchedAt
            ? `uppdaterad ${lastFetchedAt.toLocaleTimeString("sv-SE")}`
            : ""}
          testid="summary-card-total"
        />
        <SummaryCard
          label="Blueprint"
          value={byRuntime.blueprint || 0}
          hint={`Legacy: ${byRuntime.legacy || 0}`}
          testid="summary-card-blueprint"
        />
        <SummaryCard
          label="Fallbacks"
          value={summary?.fallback_count ?? 0}
          warn={warns.fallback_present}
          hint={warns.fallback_present ? "Granska orsaker" : "Inga"}
          testid="summary-card-fallback"
        />
        <SummaryCard
          label="Errors"
          value={summary?.error_count ?? 0}
          warn={warns.errors_present}
          hint={warns.errors_present ? "Kolla error_type" : "0"}
          testid="summary-card-errors"
        />
        <SummaryCard
          label="LLM-anrop"
          value={summary?.llm_called_count ?? 0}
          hint={`Actions: ${summary?.actions_rendered_count ?? 0}`}
          testid="summary-card-llm"
        />
        <SummaryCard
          label="Latens p95"
          value={`${summary?.p95_latency_ms ?? 0} ms`}
          warn={warns.high_latency_p95}
          hint={`avg ${summary?.avg_latency_ms ?? 0} · p50 ${summary?.p50_latency_ms ?? 0}`}
          testid="summary-card-latency"
        />
      </div>

      {/* H3 lead capture row */}
      <div
        className="grid grid-cols-2 md:grid-cols-3 gap-3"
        data-testid="runtime-events-leads-row"
      >
        <SummaryCard
          label="Leads skapade"
          value={summary?.leads_finalised_count ?? 0}
          hint="från blueprint-runtime"
          testid="summary-card-leads"
        />
        <SummaryCard
          label="LLM-anrop / lead"
          value={
            summary?.leads_finalised_count
              ? Math.round((summary.llm_called_count || 0) / summary.leads_finalised_count)
              : "—"
          }
          hint="konverteringsenergi"
          testid="summary-card-llm-per-lead"
        />
        <SummaryCard
          label="Lead-ratio"
          value={
            summary?.total_events
              ? `${((summary.leads_finalised_count || 0) / summary.total_events * 100).toFixed(1)}%`
              : "—"
          }
          hint={`av ${summary?.total_events ?? 0} events`}
          testid="summary-card-lead-ratio"
        />
      </div>

      {/* Scope breakdown row */}
      <div
        className="grid grid-cols-2 md:grid-cols-5 gap-3"
        data-testid="runtime-events-scope-breakdown"
      >
        <SummaryCard label="in_scope" value={byScope.in_scope || 0}
                     testid="scope-card-in" />
        <SummaryCard label="out_of_scope" value={byScope.out_of_scope || 0}
                     testid="scope-card-out" />
        <SummaryCard label="uncertain" value={byScope.uncertain || 0}
                     warn={warns.high_uncertain_ratio}
                     hint={warns.high_uncertain_ratio
                       ? `> ${Math.round((summary?.thresholds?.uncertain_warn_ratio ?? 0.3) * 100)}%`
                       : ""}
                     testid="scope-card-uncertain" />
        <SummaryCard label="do_not_guess" value={byScope.do_not_guess_match || 0}
                     testid="scope-card-dng" />
        <SummaryCard label="BP in_scope utan action"
                     value={summary?.blueprint_inscope_no_action_count ?? 0}
                     warn={warns.blueprint_inscope_without_action}
                     hint={warns.blueprint_inscope_without_action
                       ? "Möjlig saknad CTA" : "OK"}
                     testid="scope-card-no-action" />
      </div>

      {/* Filters */}
      <div
        className="rounded-lg border border-gray-200 bg-white p-4"
        data-testid="runtime-events-filters"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Filter className="w-4 h-4" /> Filter
          </div>
          <button
            type="button"
            onClick={resetFilters}
            className="text-xs text-gray-500 hover:text-[#255E69] hover:underline"
            data-testid="runtime-events-filters-reset"
          >
            Återställ
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
          <FilterInput
            label="client_id"
            testid="filter-client-id"
            value={filters.client_id}
            onChange={(v) => setFilter("client_id", v)}
            placeholder="elido_official"
          />
          <FilterSelect
            label="runtime"
            testid="filter-runtime"
            value={filters.runtime}
            onChange={(v) => setFilter("runtime", v)}
            options={RUNTIME_OPTIONS}
          />
          <FilterSelect
            label="runtime_source"
            testid="filter-runtime-source"
            value={filters.runtime_source}
            onChange={(v) => setFilter("runtime_source", v)}
            options={RUNTIME_SOURCE_OPTIONS}
          />
          <FilterSelect
            label="scope_verdict"
            testid="filter-scope-verdict"
            value={filters.scope_verdict}
            onChange={(v) => setFilter("scope_verdict", v)}
            options={SCOPE_OPTIONS}
          />
          <FilterInput
            label="fallback_reason (substring saknas — exakt match)"
            testid="filter-fallback-reason"
            value={filters.fallback_reason}
            onChange={(v) => setFilter("fallback_reason", v)}
            placeholder="t.ex. no_published_blueprint"
          />
          <FilterSelect
            label="llm_called"
            testid="filter-llm-called"
            value={filters.llm_called}
            onChange={(v) => setFilter("llm_called", v)}
            options={LLM_OPTIONS}
          />
          <FilterInput
            label="error_type"
            testid="filter-error-type"
            value={filters.error_type}
            onChange={(v) => setFilter("error_type", v)}
            placeholder="t.ex. RuntimeError"
          />
          <FilterSelect
            label="lead_finalised"
            testid="filter-lead-finalised"
            value={filters.lead_finalised}
            onChange={(v) => setFilter("lead_finalised", v)}
            options={LEAD_FINALISED_OPTIONS}
          />
          <div>
            <label
              className="block text-xs font-medium text-gray-600 mb-1"
              htmlFor="filter-time-window"
            >
              Tidsfönster
            </label>
            <select
              id="filter-time-window"
              value={filters.since_minutes}
              onChange={(e) => setFilter("since_minutes", Number(e.target.value))}
              className="w-full px-3 py-2 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#255E69]"
              data-testid="filter-since-minutes"
            >
              {TIME_WINDOWS.map((tw) => (
                <option key={tw.value} value={tw.value}>{tw.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Events table */}
      <div
        className="rounded-lg border border-gray-200 bg-white overflow-hidden"
        data-testid="runtime-events-table-wrapper"
      >
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Zap className="w-4 h-4 text-[#255E69]" />
            Senaste events ({events.length})
          </div>
          {summary?.since_iso && (
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              sedan {fmtTimestamp(summary.since_iso)}
            </div>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="runtime-events-table">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                <th className="px-3 py-2">Tidpunkt</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Runtime</th>
                <th className="px-3 py-2">Källa</th>
                <th className="px-3 py-2">Scope</th>
                <th className="px-3 py-2">Fallback / Error</th>
                <th className="px-3 py-2">Actions</th>
                <th className="px-3 py-2">LLM</th>
                <th className="px-3 py-2">Lead</th>
                <th className="px-3 py-2 text-right">Latens</th>
                <th className="px-3 py-2 text-right">msg len</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={11}
                    className="px-3 py-8 text-center text-sm text-gray-500"
                    data-testid="runtime-events-empty"
                  >
                    Inga events matchar dina filter.
                  </td>
                </tr>
              )}
              {events.map((ev) => {
                const hasErr = !!ev.error_type;
                const hasFb = !!ev.fallback_reason;
                return (
                  <tr
                    key={ev.id}
                    className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${
                      hasErr ? "bg-rose-50/40" : hasFb ? "bg-amber-50/30" : ""
                    }`}
                    onClick={() => setSelectedEvent(ev)}
                    data-testid={`runtime-event-row-${ev.id}`}
                  >
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-600 whitespace-nowrap">
                      {fmtTimestamp(ev.timestamp)}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-700 max-w-[120px] truncate">
                      {ev.client_id}
                    </td>
                    <td className="px-3 py-2">{runtimePill(ev.runtime)}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-500">
                      {ev.runtime_source || "—"}
                    </td>
                    <td className="px-3 py-2">{scopePill(ev.scope_verdict)}</td>
                    <td className="px-3 py-2 max-w-[220px]">
                      {ev.error_type ? (
                        <span className="inline-block px-1.5 py-0.5 rounded text-[11px] font-mono bg-rose-100 text-rose-700">
                          {ev.error_type}
                        </span>
                      ) : ev.fallback_reason ? (
                        <span className="text-[11px] font-mono text-amber-700">
                          {ev.fallback_reason}
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {ev.action_ids_rendered?.length ? (
                        <span className="text-[11px] font-mono text-emerald-700">
                          {ev.action_ids_rendered.length}
                        </span>
                      ) : (
                        <span className="text-gray-300">0</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {ev.llm_called
                        ? <CheckCircle2 className="inline w-3.5 h-3.5 text-emerald-600" />
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {ev.lead_finalised ? (
                        <span
                          className="inline-block px-1.5 py-0.5 rounded text-[10px] font-mono bg-emerald-100 text-emerald-700"
                          title={ev.lead_id ? `lead_id ${ev.lead_id.slice(0, 8)}` : ""}
                        >
                          ✓ lead
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] text-gray-600">
                      {ev.latency_ms || 0}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] text-gray-400">
                      {ev.message_length || 0}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel */}
      {selectedEvent && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedEvent(null)}
          data-testid="runtime-event-detail-overlay"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="runtime-event-detail-panel"
          >
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900">
                Event detalj
              </h3>
              <button
                onClick={() => setSelectedEvent(null)}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                data-testid="runtime-event-detail-close"
              >
                ✕
              </button>
            </div>
            <div className="px-5 py-4 space-y-2 text-xs">
              {Object.entries(selectedEvent).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-2">
                  <div className="text-gray-500 font-mono">{k}</div>
                  <div className="col-span-2 font-mono break-all text-gray-800">
                    {v === null || v === undefined || v === ""
                      ? <span className="text-gray-400">—</span>
                      : typeof v === "object"
                        ? JSON.stringify(v)
                        : String(v)}
                  </div>
                </div>
              ))}
              <div className="mt-3 pt-3 border-t border-gray-100 text-[11px] text-gray-500 flex items-start gap-2">
                <Shield className="w-3 h-3 mt-0.5 flex-shrink-0" />
                <div>
                  Detta event visar endast metadata. Inga råa
                  användarmeddelanden eller bot-svar lagras eller hämtas.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FilterInput({ label, value, onChange, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}
      </label>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#255E69]"
        data-testid={testid}
      />
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}
      </label>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:border-[#255E69]"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}
